"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "How do I know if my tree needs professional pruning?",
    answer: "Look for dead or crossing branches, growth that interferes with power lines, or a canopy that feels overly dense. Our arborists assess structural integrity and tree health to determine the precise cuts needed to promote long-term growth and safety for your Fort Worth property."
  }, {
    question: "Are you fully insured for residential tree removal?",
    answer: "Absolutely. Arbor Pro carries comprehensive general liability and workers' compensation insurance specifically tailored for high-risk tree operations. We protect your home, your neighbors, and our crew with every limb we touch."
  }, {
    question: "What is the best time of year for tree service in DFW?",
    answer: "While emergency removals happen year-round, structural pruning is often best performed during the dormant season (late fall to early spring). This minimizes stress on the tree and reduces the risk of pest infestations like Oak Wilt."
  }, {
    question: "Do you provide emergency storm damage response?",
    answer: "Yes. We prioritize emergency calls following North Texas storms. Our crews are equipped for rapid deployment to remove fallen trees from structures or clear blocked driveways safely and efficiently."
  }, {
    question: "How do you handle debris and stump grinding?",
    answer: "We believe in leaving your landscape better than we found it. Our standard service includes full debris hauling and chipping. Stump grinding is available as an add-on to ensure your lawn is ready for replanting or sod."
  }, {
    question: "Can you help diagnose a sick or dying tree?",
    answer: "Our certified arborists specialize in DFW-specific tree diseases and pests. We provide detailed health assessments and treatment plans, from deep-root fertilization to targeted injections, to save your valuable canopy."
  }, {
    question: "Do I need to be home during the service?",
    answer: "It is not required as long as we have clear access to the trees and any pets are secured. We provide digital updates and can review the completed work with you via phone or a scheduled walk-through."
  }, {
    question: "How do I get an accurate quote for my project?",
    answer: "You can request a quote through our website or by phone. We typically perform an on-site inspection to provide a firm, transparent price based on the tree's size, location, and the complexity of the job."
  }];
  return <section data-section-id="1442" id="common-questions" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-accent" />

      <div ref={ref} className={`mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 relative">
              {}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10 -z-0">
                <div className="absolute top-12 left-0 right-0 bottom-0 border-2 border-primary rounded-md">
                  <div className="flex border-b border-primary/60 px-3 py-2 gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">
                      ID
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">
                      Log_Entry
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary ml-auto">
                      Status
                    </span>
                  </div>
                  {[...Array(10)].map((_, i) => <div key={i} data-index={i} className="h-7 border-b border-primary/40" />)}
                </div>
              </div>

              <div className="relative z-10">
                {}
                <div className="inline-block mb-10 -rotate-[2deg]">
                  <div className="border-2 border-primary rounded-md px-4 py-2 bg-accent">
                    <div className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
                      Certified · Insured
                    </div>
                  </div>
                </div>

                {}
                <div className="mb-6 flex items-center gap-2">
                  <span className="font-mono text-xs font-medium tracking-[0.22em] uppercase text-secondary">
                    [+]
                  </span>
                  <span className="font-mono text-xs font-medium tracking-[0.22em] uppercase text-primary">
                    Phase 04 — Knowledge
                  </span>
                </div>

                {}
                <h2 className="font-default font-extrabold text-primary text-[clamp(2.25rem,4.4vw,4.25rem)] leading-[0.95] tracking-tighter mb-8">
                  Common &amp;{" "}
                  <span className="relative inline-block">
                    Questions
                    <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1.5 h-[4px] bg-accent" />
                  </span>
                </h2>

                {}
                <p className="font-default text-foreground/85 text-[clamp(1.125rem,1.5vw,1.25rem)] leading-[1.6]">
                  A technical guide to the inquiries we receive most often from
                  DFW homeowners. From surgical pruning techniques to emergency
                  storm response, these entries are verified by our ISA Certified
                  Arborists to ensure your property remains safe and thriving.
                </p>

                {}
                <div className="relative mt-10 flex items-center">
                  <span className="size-2 bg-accent shrink-0" />
                  <div className="flex-1 border-t border-dashed border-primary" />
                  <span className="size-2 bg-accent shrink-0" />
                </div>

                {}
                <div className="mt-6 font-mono text-[11px] tracking-[0.14em] text-secondary">
                  32.7555° N · 97.3308° W
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full border-t border-primary">
              {faqs.map((faq, i) => {
              const index = String(i + 1).padStart(2, "0");
              return <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b-0 group">
                    <AccordionTrigger className="hover:no-underline py-7 [&>svg]:hidden">
                      <div className="flex items-start gap-5 md:gap-8 w-full">
                        <span className="font-mono text-xs md:text-sm font-bold tracking-[0.18em] uppercase text-secondary pt-1 shrink-0 w-8">
                          {index}//
                        </span>
                        <span className="font-default font-bold text-primary text-[clamp(1.125rem,1.4vw,1.375rem)] leading-[1.2] flex-1 text-left tracking-tight">
                          {faq.question}
                        </span>
                        <span className="shrink-0 pt-1">
                          <Plus className="size-5 text-primary transition-transform duration-[380ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-data-[state=open]:rotate-45" strokeWidth={2.5} />
                        </span>
                      </div>
                    </AccordionTrigger>

                    {}
                    <div className="relative flex items-center pl-13 md:pl-16 pr-10">
                      <span className="size-1.5 bg-accent shrink-0" />
                      <div className="flex-1 border-t border-dashed border-border" />
                      <span className="size-1.5 bg-accent shrink-0" />
                    </div>

                    <AccordionContent className="pt-6 pb-8">
                      <div className="flex gap-5 md:gap-8">
                        <span className="w-8 shrink-0" aria-hidden="true" />
                        <p className="font-default text-foreground/85 text-base md:text-lg leading-[1.5] flex-1 pr-10">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}